/**
 * AICopilot - Unified AI Assistant Panel
 *
 * BYK-165: Create AI Copilot sidebar component
 *
 * Consolidates all AI features into a single interface:
 * - Layout suggestions (work triangle optimization)
 * - Render requests (AI-powered visualization)
 * - Design tips (material recommendations, Kashrut compliance)
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Bot, User, Loader2, Layout, Image, Lightbulb, Camera, ChevronDown, Download, Eye } from "lucide-react";
import { useFloorPlanStore } from "@/stores/floorPlan";
import { useCanvasRegistry } from "@/stores/canvasRegistry";
import { generateFallbackLayouts } from "@/services/layoutEngine";
import { downloadExport, formatFileSize, type ExportResult } from "@/services/canvasExport";
import {
  classifyLayoutIntent,
  classifyRenderIntent,
  classifyTipsIntent,
  getRenderStyleFromIntent,
} from "@/services/intentClassifier";
import {
  createRenderWithReferences,
  getRenderImageUrl,
  waitForRender,
  type RenderStyle,
  type RenderCamera,
  type RenderLighting,
} from "@/services/renders";
import { getOrCreateDefaultProject } from "@/services/projects";
import { LayoutPreview } from "./LayoutPreview";
import { cn } from "@/lib/utils";
import type { LayoutSuggestion } from "@byk/shared";

// ============================================================================
// Types
// ============================================================================

type CopilotMode = "layout" | "render" | "tips";
type MessageRole = "user" | "assistant" | "system";

interface ChatMessage {
  id: string;
  role: MessageRole;
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  mode: CopilotMode;
  suggestions?: LayoutSuggestion[];
  appliedSuggestionId?: string;
  renderUrl?: string;
}

interface ModeTab {
  id: CopilotMode;
  label: string;
  icon: React.ReactNode;
  description: string;
}

// ============================================================================
// Constants
// ============================================================================

const MODE_TABS: ModeTab[] = [
  {
    id: "layout",
    label: "Layout",
    icon: <Layout className="h-4 w-4" />,
    description: "Optimize your kitchen layout",
  },
  {
    id: "render",
    label: "Render",
    icon: <Image className="h-4 w-4" />,
    description: "Generate photorealistic renders",
  },
  {
    id: "tips",
    label: "Tips",
    icon: <Lightbulb className="h-4 w-4" />,
    description: "Get design recommendations",
  },
];

const QUICK_PROMPTS: Record<CopilotMode, { label: string; prompt: string }[]> = {
  layout: [
    { label: "Optimize layout", prompt: "Suggest an optimal kitchen layout" },
    { label: "Work triangle", prompt: "Analyze my work triangle efficiency" },
    { label: "Kashrut zones", prompt: "Check for Kashrut zone compliance" },
  ],
  render: [
    { label: "Day scene", prompt: "Render with natural daylight" },
    { label: "Evening", prompt: "Render with warm evening lighting" },
    { label: "Studio", prompt: "Render with professional studio lighting" },
  ],
  tips: [
    { label: "Storage", prompt: "Give me storage optimization tips" },
    { label: "Materials", prompt: "Recommend materials for durability" },
    { label: "Accessibility", prompt: "Check accessibility guidelines" },
  ],
};

const MODE_WELCOME: Record<CopilotMode, string> = {
  layout: "I can help optimize your kitchen layout using the Work Triangle principle. Ask me to suggest layouts or analyze your current arrangement.",
  render: "I can generate photorealistic renders of your kitchen design. Describe the lighting and style you'd like to see.",
  tips: "I can provide design tips, material recommendations, and compliance checks. What would you like to know?",
};

// ============================================================================
// Message Bubble Component
// ============================================================================

interface MessageBubbleProps {
  message: ChatMessage;
  onApplyLayout: (layout: LayoutSuggestion) => void;
}

function MessageBubble({ message, onApplyLayout }: MessageBubbleProps) {
  const isUser = message.role === "user";
  const isSystem = message.role === "system";

  if (isSystem) {
    return (
      <div className="flex justify-center">
        <div className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-500">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn("flex gap-2", isUser ? "flex-row-reverse" : "flex-row")}
    >
      {/* Avatar */}
      <div
        className={cn(
          "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full",
          isUser ? "bg-blue-100" : "bg-purple-100"
        )}
      >
        {isUser ? (
          <User className="h-3.5 w-3.5 text-blue-600" />
        ) : (
          <Bot className="h-3.5 w-3.5 text-purple-600" />
        )}
      </div>

      {/* Content */}
      <div
        className={cn(
          "flex max-w-[85%] flex-col gap-2",
          isUser ? "items-end" : "items-start"
        )}
      >
        <div
          className={cn(
            "rounded-2xl px-3 py-2 text-xs",
            isUser
              ? "bg-blue-600 text-white"
              : "bg-gray-100 text-gray-800"
          )}
        >
          {message.isStreaming ? (
            <span className="flex items-center gap-2">
              {message.content}
              <span className="inline-flex">
                <span className="animate-bounce">.</span>
                <span className="animate-bounce" style={{ animationDelay: "0.1s" }}>.</span>
                <span className="animate-bounce" style={{ animationDelay: "0.2s" }}>.</span>
              </span>
            </span>
          ) : (
            <span className="whitespace-pre-wrap">{message.content}</span>
          )}
        </div>

        {/* Layout Suggestions */}
        {message.suggestions && message.suggestions.length > 0 && (
          <div className="w-full space-y-2">
            {message.suggestions.map((layout, index) => (
              <LayoutPreview
                key={layout.id}
                layout={layout}
                rank={index + 1}
                isApplied={message.appliedSuggestionId === layout.id}
                onApply={() => onApplyLayout(layout)}
                compact
              />
            ))}
          </div>
        )}

        {/* Render Preview */}
        {message.renderUrl && (
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <img
              src={message.renderUrl}
              alt="AI Generated Render"
              className="w-full"
            />
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ============================================================================
// Mode Tab Button
// ============================================================================

interface ModeTabButtonProps {
  tab: ModeTab;
  isActive: boolean;
  onClick: () => void;
}

function ModeTabButton({ tab, isActive, onClick }: ModeTabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex flex-1 flex-col items-center gap-0.5 rounded-lg px-2 py-1.5 text-xs font-medium transition-all",
        isActive
          ? "bg-blue-600 text-white"
          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
      )}
      aria-pressed={isActive}
    >
      {tab.icon}
      <span className="text-[10px]">{tab.label}</span>
    </button>
  );
}

// ============================================================================
// Reference Image Panel (BYK-177)
// ============================================================================

interface ReferenceImagePanelProps {
  onExportComplete?: (exports: { floorPlan?: ExportResult; whiteModel?: ExportResult }) => void;
}

function ReferenceImagePanel({ onExportComplete }: ReferenceImagePanelProps) {
  const [expanded, setExpanded] = useState(false);
  const [include2D, setInclude2D] = useState(true);
  const [include3D, setInclude3D] = useState(true);
  const [preview2D, setPreview2D] = useState<ExportResult | null>(null);
  const [preview3D, setPreview3D] = useState<ExportResult | null>(null);

  const { export2D, export3DWhite, exportState } = useCanvasRegistry();

  // Generate preview exports
  const handleGeneratePreview = useCallback(async () => {
    const results: { floorPlan?: ExportResult; whiteModel?: ExportResult } = {};

    if (include2D) {
      const result = await export2D({ width: 800, height: 600 });
      if (result) {
        setPreview2D(result);
        results.floorPlan = result;
      }
    }

    if (include3D) {
      const result = await export3DWhite({ width: 800, height: 600 });
      if (result) {
        setPreview3D(result);
        results.whiteModel = result;
      }
    }

    onExportComplete?.(results);
  }, [include2D, include3D, export2D, export3DWhite, onExportComplete]);

  // Download individual export
  const handleDownload = useCallback((result: ExportResult, name: string) => {
    downloadExport(result, `byk-${name}-${Date.now()}.png`);
  }, []);

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          <Camera className="h-4 w-4 text-blue-600" />
          <span className="text-xs font-medium text-gray-700">Reference Images</span>
        </div>
        <ChevronDown
          className={cn(
            "h-4 w-4 text-gray-400 transition-transform",
            expanded && "rotate-180"
          )}
        />
      </button>

      {/* Content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="border-t border-gray-100 px-3 py-2 space-y-3">
              {/* Toggles */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={include2D}
                    onChange={(e) => setInclude2D(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-600">Include 2D Floor Plan</span>
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={include3D}
                    onChange={(e) => setInclude3D(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-600">Include 3D White Model</span>
                </label>
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGeneratePreview}
                disabled={(!include2D && !include3D) || exportState.isExporting}
                className={cn(
                  "flex w-full items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium",
                  "bg-blue-600 text-white",
                  "hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed",
                  "transition-colors"
                )}
              >
                {exportState.isExporting ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Eye className="h-3 w-3" />
                    Generate Preview
                  </>
                )}
              </button>

              {/* Error */}
              {exportState.exportError && (
                <div className="text-xs text-red-600 bg-red-50 rounded px-2 py-1">
                  {exportState.exportError}
                </div>
              )}

              {/* Previews */}
              {(preview2D || preview3D) && (
                <div className="space-y-2">
                  {preview2D && (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-medium text-gray-500">2D Floor Plan</span>
                        <button
                          onClick={() => handleDownload(preview2D, "floorplan")}
                          className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-700"
                        >
                          <Download className="h-3 w-3" />
                          {formatFileSize(preview2D.sizeBytes)}
                        </button>
                      </div>
                      <img
                        src={preview2D.dataUrl}
                        alt="2D Floor Plan Preview"
                        className="w-full rounded border border-gray-200"
                      />
                    </div>
                  )}
                  {preview3D && (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-medium text-gray-500">3D White Model</span>
                        <button
                          onClick={() => handleDownload(preview3D, "whitemodel")}
                          className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-700"
                        >
                          <Download className="h-3 w-3" />
                          {formatFileSize(preview3D.sizeBytes)}
                        </button>
                      </div>
                      <img
                        src={preview3D.dataUrl}
                        alt="3D White Model Preview"
                        className="w-full rounded border border-gray-200"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Info Text */}
              <p className="text-[10px] text-gray-400">
                Reference images help AI understand your exact layout for accurate renders.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function AICopilot() {
  const { room, walls, elements, volumes, applyLayoutSuggestion } =
    useFloorPlanStore();

  const [mode, setMode] = useState<CopilotMode>("layout");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // BYK-177: Reference image exports for AI grounding
  const [referenceExports, setReferenceExports] = useState<{
    floorPlan?: ExportResult;
    whiteModel?: ExportResult;
  }>({});

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Track which modes have shown welcome messages to prevent duplicates
  const welcomeShownRef = useRef<Set<CopilotMode>>(new Set());

  // Generate unique ID
  const generateId = () => `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Add welcome message when mode changes (fixed: removed messages from deps to prevent render issues)
  useEffect(() => {
    // Only add welcome message once per mode using ref to track
    if (!welcomeShownRef.current.has(mode)) {
      welcomeShownRef.current.add(mode);
      setMessages((prev) => [
        ...prev,
        {
          id: generateId(),
          role: "assistant",
          content: MODE_WELCOME[mode],
          timestamp: new Date(),
          mode,
        },
      ]);
    }
  }, [mode]);

  // Add message helper
  const addMessage = useCallback((message: Omit<ChatMessage, "id" | "timestamp">) => {
    const newMessage: ChatMessage = {
      ...message,
      id: generateId(),
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newMessage]);
    return newMessage.id;
  }, []);

  // Update message helper
  const updateMessage = useCallback((id: string, updates: Partial<ChatMessage>) => {
    setMessages((prev) =>
      prev.map((msg) => (msg.id === id ? { ...msg, ...updates } : msg))
    );
  }, []);

  // Handle layout suggestions
  const handleLayoutSuggestion = useCallback(async () => {
    setIsLoading(true);

    const assistantId = addMessage({
      role: "assistant",
      content: "Analyzing your kitchen layout",
      isStreaming: true,
      mode: "layout",
    });

    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const result = generateFallbackLayouts(room, walls, elements, volumes);

      if (result.layouts.length > 0) {
        updateMessage(assistantId, {
          content: `I found ${result.layouts.length} optimized layout${result.layouts.length > 1 ? "s" : ""} for your kitchen. ${result.usedFallback ? "(Using rule-based suggestions)" : ""} Here are my recommendations:`,
          isStreaming: false,
          suggestions: result.layouts,
        });
      } else {
        updateMessage(assistantId, {
          content:
            "I couldn't generate layout suggestions. Add sink, cooktop, and refrigerator to enable optimization.",
          isStreaming: false,
        });
      }
    } catch (err) {
      console.error("Layout suggestion failed:", err);
      updateMessage(assistantId, {
        content: "Sorry, I encountered an error. Please try again.",
        isStreaming: false,
      });
    } finally {
      setIsLoading(false);
    }
  }, [room, walls, elements, volumes, addMessage, updateMessage]);

  // Handle render request - BYK-177: Supports reference image grounding
  const handleRenderRequest = useCallback(async (styleInput: string) => {
    setIsLoading(true);

    // Map style input to render parameters
    const lightingMap: Record<string, RenderLighting> = {
      daylight: "daylight",
      "natural daylight": "daylight",
      evening: "evening",
      "warm evening": "evening",
      studio: "studio",
      "professional studio": "studio",
      dramatic: "dramatic",
    };
    const lighting: RenderLighting = lightingMap[styleInput.toLowerCase()] || "daylight";

    const assistantId = addMessage({
      role: "assistant",
      content: `Generating ${styleInput} render${referenceExports.floorPlan || referenceExports.whiteModel ? " with reference images" : ""}...`,
      isStreaming: true,
      mode: "render",
    });

    try {
      // Get or create a project for the render
      updateMessage(assistantId, {
        content: `Preparing project for render...`,
      });
      const project = await getOrCreateDefaultProject({
        room: { width: room.width, height: room.depth },
        volumes,
        walls,
      });

      updateMessage(assistantId, {
        content: `Generating ${styleInput} render${referenceExports.floorPlan || referenceExports.whiteModel ? " with reference images" : ""}...`,
      });

      // Build render request with reference images
      const renderResult = await createRenderWithReferences({
        projectId: project.id,
        style: "photorealistic" as RenderStyle,
        cameraAngle: "eye-level" as RenderCamera,
        lightingMood: lighting,
        floorPlanDataUrl: referenceExports.floorPlan?.dataUrl,
        whiteModelDataUrl: referenceExports.whiteModel?.dataUrl,
      });

      // Wait for render completion (poll status)
      const completedRender = await waitForRender(renderResult.render.id, {
        pollInterval: 2000,
        maxAttempts: 60,
        onProgress: (render) => {
          if (render.status === "processing") {
            updateMessage(assistantId, {
              content: `Generating ${styleInput} render... AI is creating your visualization.`,
            });
          }
        },
      });

      if (completedRender.status === "completed") {
        const imageUrl = getRenderImageUrl(completedRender.id);
        updateMessage(assistantId, {
          content: `Here's your kitchen with ${styleInput} lighting. The render shows your current layout with photorealistic materials.`,
          isStreaming: false,
          renderUrl: imageUrl,
        });
      } else {
        throw new Error(completedRender.error || "Render generation failed");
      }
    } catch (err) {
      console.error("Render generation failed:", err);
      updateMessage(assistantId, {
        content: `Sorry, I couldn't generate the render. ${err instanceof Error ? err.message : "Please try again."}`,
        isStreaming: false,
      });
    } finally {
      setIsLoading(false);
    }
  }, [addMessage, updateMessage, room, volumes, walls, referenceExports]);

  // Handle tips request
  const handleTipsRequest = useCallback(async (topic: string) => {
    setIsLoading(true);

    const assistantId = addMessage({
      role: "assistant",
      content: `Gathering ${topic} recommendations`,
      isStreaming: true,
      mode: "tips",
    });

    await new Promise((resolve) => setTimeout(resolve, 800));

    const tips: Record<string, string> = {
      storage: `Storage Optimization Tips:

• Place frequently used items at counter height (85-120cm)
• Use corner solutions like lazy Susans or magic corners
• Store heavy items in lower cabinets to prevent accidents
• Keep cooking utensils near the cooktop
• Install pull-out drawers in deep base cabinets
• Use vertical dividers for baking sheets and cutting boards`,
      materials: `Material Recommendations:

• Countertops: Quartz for durability, granite for heat resistance
• Cabinets: Solid wood or high-quality MDF with proper edge banding
• Backsplash: Porcelain tiles or tempered glass for easy cleaning
• Flooring: Porcelain tiles or vinyl for water resistance
• Hardware: Stainless steel or brushed nickel for longevity`,
      accessibility: `Accessibility Guidelines:

• Ensure 120cm minimum clearance between counters
• Install varied counter heights (75-90cm) for seated work
• Use D-shaped or loop handles for easy grip
• Provide knee clearance under sink and cooktop
• Install pull-out shelves in base cabinets
• Consider touch-activated faucets`,
      kashrut: `Kashrut Compliance Check:

• Meat/dairy separation: Requires 60cm minimum between zones
• Dual sinks: Consider separate sinks for meat and dairy
• Storage zones: Designate separate cabinets for each category
• Counter space: Provide distinct prep areas for each type
• Dishwasher: Consider dual units or scheduling protocols`,
    };

    const response = tips[topic.toLowerCase()] || `Here are some ${topic} tips for your kitchen design. Would you like more specific recommendations?`;

    updateMessage(assistantId, {
      content: response,
      isStreaming: false,
    });

    setIsLoading(false);
  }, [addMessage, updateMessage]);

  // Handle send message using intent classifiers
  const handleSendMessage = useCallback(async () => {
    const trimmedInput = input.trim();
    if (!trimmedInput || isLoading) return;

    addMessage({ role: "user", content: trimmedInput, mode });
    setInput("");

    if (mode === "layout") {
      const { intent } = classifyLayoutIntent(trimmedInput);
      if (intent !== "unknown") {
        await handleLayoutSuggestion();
      } else {
        // Default layout response
        setIsLoading(true);
        const id = addMessage({
          role: "assistant",
          content: "Thinking",
          isStreaming: true,
          mode: "layout",
        });
        await new Promise((r) => setTimeout(r, 400));
        updateMessage(id, {
          content: "I can help with layout optimization, work triangle analysis, and Kashrut zone planning. What would you like me to focus on?",
          isStreaming: false,
        });
        setIsLoading(false);
      }
    } else if (mode === "render") {
      const { intent } = classifyRenderIntent(trimmedInput);
      const style = intent === "custom" ? trimmedInput : getRenderStyleFromIntent(intent);
      await handleRenderRequest(style);
    } else if (mode === "tips") {
      const { intent } = classifyTipsIntent(trimmedInput);
      await handleTipsRequest(intent === "custom" ? trimmedInput : intent);
    }
  }, [
    input,
    mode,
    isLoading,
    addMessage,
    updateMessage,
    handleLayoutSuggestion,
    handleRenderRequest,
    handleTipsRequest,
  ]);

  // Handle quick prompt click
  const handleQuickPrompt = useCallback((prompt: string) => {
    setInput(prompt);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  // Handle layout apply
  const handleApplyLayout = useCallback(
    (layout: LayoutSuggestion) => {
      applyLayoutSuggestion(layout);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.suggestions?.some((s) => s.id === layout.id)
            ? { ...msg, appliedSuggestionId: layout.id }
            : msg
        )
      );
      addMessage({
        role: "system",
        content: `Applied "${layout.type}" layout`,
        mode: "layout",
      });
    },
    [applyLayoutSuggestion, addMessage]
  );

  // Handle key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Filter messages for current mode
  const filteredMessages = messages.filter((m) => m.mode === mode);

  return (
    <div className="flex h-full flex-col">
      {/* Mode Tabs */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-white p-2">
        <div className="flex gap-1">
          {MODE_TABS.map((tab) => (
            <ModeTabButton
              key={tab.id}
              tab={tab}
              isActive={mode === tab.id}
              onClick={() => setMode(tab.id)}
            />
          ))}
        </div>
      </div>

      {/* BYK-177: Reference Image Panel for Render Mode */}
      {mode === "render" && (
        <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50 px-3 py-2">
          <ReferenceImagePanel onExportComplete={setReferenceExports} />
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-3" style={{ overscrollBehaviorY: 'contain' }}>
        <div className="space-y-3">
          <AnimatePresence>
            {filteredMessages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                onApplyLayout={handleApplyLayout}
              />
            ))}
          </AnimatePresence>
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Quick Prompts */}
      {filteredMessages.length <= 2 && (
        <div className="flex-shrink-0 border-t bg-gray-50 px-3 py-2">
          <div className="flex flex-wrap gap-1.5">
            {QUICK_PROMPTS[mode].map((action) => (
              <button
                key={action.label}
                onClick={() => handleQuickPrompt(action.prompt)}
                className={cn(
                  "rounded-full bg-white px-2.5 py-1 text-[10px] font-medium",
                  "border border-gray-200 text-gray-600",
                  "hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600",
                  "transition-colors"
                )}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="flex-shrink-0 border-t bg-white p-2">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Ask about ${mode}...`}
            disabled={isLoading}
            className={cn(
              "flex-1 rounded-full border border-gray-200 px-3 py-1.5 text-xs",
              "focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100",
              "disabled:bg-gray-50 disabled:text-gray-400"
            )}
          />
          <button
            onClick={handleSendMessage}
            disabled={!input.trim() || isLoading}
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-full",
              "bg-blue-600 text-white",
              "hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed",
              "transition-colors"
            )}
            aria-label="Send message"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AICopilot;
